import { html as cmHtml } from '@codemirror/lang-html';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CodeMirror from '@uiw/react-codemirror';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { TemplateDetail, VariableSchemaEntry } from '../api/client';
import { useAuth } from '../auth/AuthProvider';

export function TemplateEditorPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { fetcher } = useAuth();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['template', id],
    queryFn: () => fetcher<TemplateDetail>(`/api/templates/${id}`),
    enabled: !!id,
  });

  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [schema, setSchema] = useState<VariableSchemaEntry[]>([]);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    if (q.data) {
      setBody(q.data.body);
      setName(q.data.name);
      setSchema(q.data.variable_schema ?? []);
    }
  }, [q.data]);

  const saveM = useMutation({
    mutationFn: () =>
      fetcher<TemplateDetail>(`/api/templates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, body, variable_schema: schema }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['template', id] }),
  });

  const previewM = useMutation({
    mutationFn: () => {
      // Generate sample merge_data from the schema defaults.
      const sample: Record<string, string> = {};
      for (const v of schema) {
        sample[v.name] = v.default ?? `sample ${v.name}`;
      }
      return fetcher<{ html: string }>(`/api/templates/${id}/preview`, {
        method: 'POST',
        body: JSON.stringify({ merge_data: sample }),
      });
    },
    onSuccess: (res) => setPreviewHtml(res.html),
  });

  // Auto-render preview after data first arrives.
  useEffect(() => {
    if (q.data) previewM.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data]);

  const previewSrcDoc = useMemo(() => previewHtml, [previewHtml]);

  if (q.isLoading) return <p>Loading…</p>;
  if (q.isError) return <p className="text-red-600">Failed to load.</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <input
          className="input max-w-md"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex gap-2">
          <button
            type="button"
            className="btn"
            onClick={() => previewM.mutate()}
          >
            Refresh preview
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={saveM.isPending}
            onClick={() => saveM.mutate()}
          >
            Save
          </button>
        </div>
      </div>
      {saveM.isError ? (
        <p className="text-sm text-red-600">
          Save failed: {(saveM.error as Error).message}
        </p>
      ) : null}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card">
          <h2 className="mb-2 text-sm font-medium">HTML body</h2>
          <CodeMirror
            value={body}
            height="420px"
            extensions={[cmHtml()]}
            onChange={(v) => setBody(v)}
          />
        </div>
        <div className="card">
          <h2 className="mb-2 text-sm font-medium">Preview</h2>
          <iframe
            title="template preview"
            sandbox=""
            srcDoc={previewSrcDoc}
            className="h-[420px] w-full rounded border border-slate-200"
          />
        </div>
      </div>
      <div className="card">
        <h2 className="mb-2 text-sm font-medium">Variables</h2>
        <SchemaEditor schema={schema} onChange={setSchema} />
      </div>
    </div>
  );
}

function SchemaEditor({
  schema,
  onChange,
}: {
  schema: VariableSchemaEntry[];
  onChange: (next: VariableSchemaEntry[]) => void;
}) {
  function update(i: number, patch: Partial<VariableSchemaEntry>) {
    const next = schema.slice();
    const existing = next[i];
    if (!existing) return;
    next[i] = { ...existing, ...patch };
    onChange(next);
  }
  function remove(i: number) {
    onChange(schema.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([
      ...schema,
      { name: `var_${schema.length + 1}`, label: '', required: false },
    ]);
  }
  return (
    <div className="space-y-2">
      {schema.map((v, i) => (
        <div key={i} className="grid grid-cols-12 items-center gap-2">
          <input
            className="input col-span-3"
            placeholder="name"
            value={v.name}
            onChange={(e) => update(i, { name: e.target.value })}
          />
          <input
            className="input col-span-4"
            placeholder="label"
            value={v.label ?? ''}
            onChange={(e) => update(i, { label: e.target.value })}
          />
          <input
            className="input col-span-3"
            placeholder="default"
            value={v.default ?? ''}
            onChange={(e) => update(i, { default: e.target.value })}
          />
          <label className="col-span-1 flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={!!v.required}
              onChange={(e) => update(i, { required: e.target.checked })}
            />
            req
          </label>
          <button
            type="button"
            className="btn-danger col-span-1"
            onClick={() => remove(i)}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="btn" onClick={add}>
        Add variable
      </button>
    </div>
  );
}
